/**
 * Felt templates — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_craft`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const feltTemplatesPack: ToilePack = {
    slug: 'felt-templates',
    category: 'craft',
    name: 'Felt templates',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fox', label: 'Fox plush', subject: 'a sitting fox plush — body, tail, two ears, a muzzle and two cheek patches — with the finished toy sketched small in one corner'},
        {key: 'food', label: 'Play food', subject: 'a set of felt play food, a ring donut with a dripping icing top, a slice of layer cake and a strawberry with its calyx'},
        {key: 'gnome', label: 'Gnome ornament', subject: 'a Scandinavian gnome ornament, a tall pointed hat, a wide beard, a round nose and a narrow hanging loop'},
    ],
    styles: [
        {key: 'plain', label: 'Cutting sheet', style: 'a plain cutting sheet, each piece a single closed outline with nothing drawn inside it, the pieces set out in tidy rows', palette: 'black ink only', framing: 'The pieces laid out in rows with a wide white margin all round'},
        {key: 'sewing', label: 'Sewing lines', style: 'a sewing-line sheet, every piece carrying a solid cut line with a dashed stitching line inside it, notches, dot placement marks and small arrows showing which edge joins which', palette: 'black ink only', framing: 'The pieces spread across the full page down to a narrow white margin'},
        {key: 'nested', label: 'Nested sheet', style: 'an economy sheet, the pieces nested close together to spare the felt, each one tagged with its felt colour in small capitals', palette: 'black ink only', framing: 'The pieces packed into the upper two-thirds, the notes in a strip below'},
    ],
    directions: ['a bunny instead', 'as a baby mobile', 'for a Christmas garland'],
    seo: {
        title: 'Felt templates: fox plush, play food, gnome',
        metaDescription: 'Nine felt templates — fox plush, play food and gnome ornament — as a plain cutting sheet, sewing lines, or a felt-saving nested sheet.',
        h1: 'Felt templates: fox plush, play food and gnome',
        tagline: 'Nine sheets, three toys, three cutting styles each — pieces numbered with a cut-quantity note beside each one.',
        intro: 'A felt template hands over the cutting pieces, actual size, ready to pin to the felt. These nine template a sitting fox plush, a set of felt play food and a Scandinavian gnome ornament, each laid out three ways — a plain cutting sheet, a version with sewing lines and notches, and a nested sheet that spares the felt. Every piece is numbered.',
        useCases: [
            'a first felt-sewing project',
            'a set of felt vegetables for a play kitchen',
            'a Christmas garland ornament',
            'a baby-mobile shape',
        ],
        faq: [
            {q: 'What size does the template print at?', a: 'Portrait A4 proportions with a margin, each piece drawn at actual cutting size.'},
            {q: 'Does this include a stitching guide?', a: 'The sewing-lines style adds a dashed stitch line and notches; the plain cutting sheet gives outlines only.'},
            {q: 'Can I get a different toy or shape?', a: 'Yes — use the direction field to ask for a bunny or another shape, laid out the same three ways.'},
        ],
    },
}
